import * as React from 'react';

import type {EntityCustomFieldType} from 'nutshell-graphql-types';

import {Label} from '../form/label';
import {StackLegacy} from '../layout/stack/stack-legacy';
import {SelectPickerGeneric} from '../select-picker-generic/select-picker-generic';
import {SelectTwoLine} from '../select-two-line/select-two-line';
import {TextField} from '../textfield/textfield';

import {
    ENTITY_TYPE_SELECT_OPTIONS,
    ALL_ENTITY_CUSTOM_FIELD_SELECT_OPTIONS,
    LEAD_CUSTOM_FIELD_SELECT_OPTIONS,
} from './consts';
import {createCustomFieldReducer} from './create-custom-field-reducer';
import {CreateDecisionFieldChoices} from './create-decision-field-choices';
import type {
    CustomFieldEntity,
    CustomFieldEntityOption,
    CustomFieldTypeOption,
    CreateCustomFieldResponse,
} from './types';

type Props = {
    onCreate: (arg1: {
        name: string;
        type: EntityCustomFieldType;
        entityName: CustomFieldEntity;
        choices?: Array<string>;
        allowMultiple?: boolean;
    }) => Promise<any>;
    createButtonRef?: any;
    onCreateSuccess?: (response: CreateCustomFieldResponse) => any;
    onCreateError?: (arg1?: any) => any;
    // Optional pass in editable name for field
    name?: string;
    // Optional predefined properties of the custom field (non-editable)
    entityType?: CustomFieldEntity;
    customFieldType?: EntityCustomFieldType;
    enumIsAllowMultiple?: boolean;
};

export function CreateCustomField(props: Props) {
    const {
        name,
        customFieldType,
        entityType,
        enumIsAllowMultiple,
        createButtonRef,
        onCreate,
        onCreateSuccess,
        onCreateError,
    } = props;

    const [state, dispatch] = React.useReducer(createCustomFieldReducer, {
        name: name ? name : '',
        // @ts-expect-error
        type: customFieldType ? customFieldType : ALL_ENTITY_CUSTOM_FIELD_SELECT_OPTIONS[0].value,
        // @ts-expect-error
        entityName: entityType ? entityType : ENTITY_TYPE_SELECT_OPTIONS[0].value,
        choices: [],
        allowMultiple: Boolean(enumIsAllowMultiple),
        description: '',
    });

    // Checking the validity of the custom field
    let isCustomFieldValid = Boolean(state.name.trim());
    if (state.type === 'ENUM' && !state.choices.length) {
        isCustomFieldValid = false;
    }

    // Create a ref for name textfield, description textfield, and a hidden create button
    const nameTextfieldRef = React.useRef(undefined);
    const descriptionTextfieldRef = React.useRef(undefined);
    const hiddenCreateButtonRef = React.useRef(undefined);

    // Custom field select options differ between entity types
    const customFieldSelectOptions = [...ALL_ENTITY_CUSTOM_FIELD_SELECT_OPTIONS];
    if (state.entityName === 'Leads') {
        customFieldSelectOptions.push(...LEAD_CUSTOM_FIELD_SELECT_OPTIONS);
    }

    // If entity type changes, previously selected option may not be available for entity
    const selectedCustomFieldOption = customFieldSelectOptions.find(
        (fieldType) => fieldType.value === state.type
    );
    if (!selectedCustomFieldOption) {
        dispatch({
            type: 'setType',
            // @ts-expect-error
            customFieldType: customFieldSelectOptions[0].value,
        });
    }

    // Effect to autoFocus the field name input because autoFocus prop doesn't work
    React.useEffect(() => {
        if (nameTextfieldRef && nameTextfieldRef.current) {
            // @ts-expect-error
            nameTextfieldRef.current.focus();
        }
    }, []);

    // Set up a listener when create button is clicked (if one is passed)
    React.useEffect(() => {
        if (createButtonRef && createButtonRef.current) {
            createButtonRef.current.addEventListener('click', () => {
                if (
                    nameTextfieldRef &&
                    nameTextfieldRef.current &&
                    descriptionTextfieldRef &&
                    descriptionTextfieldRef.current &&
                    hiddenCreateButtonRef &&
                    hiddenCreateButtonRef.current
                ) {
                    // Because an 'Enter' keyPress event cannot be triggered,
                    // we need to use a hidden button to simulate a click event
                    // to trigger the onCreate - which due to state dependencies
                    // cannot appear in a useEffect, or outside the component
                    // @ts-expect-error
                    hiddenCreateButtonRef.current.click();
                }
            });
        }
    }, [createButtonRef]);

    // Effect to toggle disabling the create button based on custom field validity
    React.useEffect(() => {
        setCreateButtonIsDisabled(createButtonRef, !isCustomFieldValid);
    }, [createButtonRef, isCustomFieldValid]);

    // Handler for creating a custom field
    function handleOnCreate() {
        // Double-check validity of custom field
        if (!isCustomFieldValid) return;

        // Disable submit
        setCreateButtonIsDisabled(createButtonRef, true);

        // Create custom field
        onCreate({
            ...state,
            // For non-enum fields, choices and allowMultiple should be undefined
            choices: state.type === 'ENUM' ? state.choices : undefined,
            allowMultiple: state.type === 'ENUM' ? state.allowMultiple : undefined,
        })
            .then((response) => {
                // Enable submit
                setCreateButtonIsDisabled(createButtonRef, false);
                if (onCreateSuccess)
                    onCreateSuccess({
                        customField: response,
                        entity: state.entityName,
                        customFieldType: state.type,
                    });
            })
            .catch((error) => {
                // Enable submit
                setCreateButtonIsDisabled(createButtonRef, false);
                if (onCreateError) onCreateError(error);
            });
    }

    return (
        <>
            <StackLegacy spacing={16}>
                <div>
                    <Label htmlFor='field-name' required={true}>
                        Field name
                    </Label>
                    <TextField
                        // @ts-expect-error
                        name='field-name'
                        placeholder='Field name'
                        onEnter={handleOnCreate}
                        textFieldRef={(ref) => {
                            // @ts-expect-error
                            nameTextfieldRef.current = ref;
                        }}
                        onChange={(value) => {
                            dispatch({type: 'setName', name: value});
                        }}
                        value={state.name}
                    />
                </div>
                {!props.entityType ? (
                    <div>
                        <Label htmlFor='entity-type'>Entity</Label>
                        <SelectPickerGeneric
                            name='entity-type'
                            isSearchable={false}
                            options={ENTITY_TYPE_SELECT_OPTIONS}
                            value={ENTITY_TYPE_SELECT_OPTIONS.find(
                                (entity: CustomFieldEntityOption) =>
                                    entity.value === state.entityName
                            )}
                            onChange={(selectedEntity: CustomFieldEntityOption) => {
                                dispatch({
                                    type: 'setEntityName',
                                    entityName: selectedEntity.value,
                                });
                            }}
                        />
                    </div>
                ) : // Otherwise entity type is predefined
                undefined}
                {!props.customFieldType ? (
                    <div>
                        <Label htmlFor='custom-field-type'>Field type</Label>
                        <SelectTwoLine
                            name='custom-field-type'
                            isSearchable={false}
                            options={customFieldSelectOptions}
                            value={selectedCustomFieldOption}
                            onChange={(selectedFieldType: CustomFieldTypeOption) => {
                                dispatch({
                                    type: 'setType',
                                    customFieldType: selectedFieldType.value,
                                });
                            }}
                        />
                    </div>
                ) : // Otherwise field type is predefined
                undefined}
                {state.type === 'ENUM' ? (
                    <CreateDecisionFieldChoices
                        choices={state.choices}
                        onUpdateChoices={(choices: string[]) =>
                            dispatch({type: 'updateChoices', choices})
                        }
                        hasAllowMultipleSelection={
                            typeof props.enumIsAllowMultiple === 'undefined'
                                ? {
                                      allowMultiple: state.allowMultiple,
                                      toggleAllowMultiple: () =>
                                          dispatch({type: 'toggleAllowMultiple'}),
                                  }
                                : // Otherwise allow multiple is predefined
                                  undefined
                        }
                    />
                ) : // Non-enum type is currently selected
                undefined}
                <div>
                    <Label htmlFor='field-description'>Field description</Label>
                    <TextField
                        // @ts-expect-error
                        name='field-description'
                        placeholder='Field description'
                        onEnter={handleOnCreate}
                        multiline={true}
                        textFieldRef={(ref) => {
                            // @ts-expect-error
                            descriptionTextfieldRef.current = ref;
                        }}
                        onChange={(value) => {
                            dispatch({type: 'setDescription', description: value});
                        }}
                        value={state.description}
                    />
                </div>
            </StackLegacy>
            {/* Hidden button used to trigger onCreate via external button click */}
            <button
                type='button'
                style={{display: 'none', visibility: 'hidden'}}
                onClick={handleOnCreate}
                disabled={!isCustomFieldValid}
                // @ts-expect-error
                ref={hiddenCreateButtonRef}
            >
                Create
            </button>
        </>
    );
}

// Function outside of scope to set the disabled status external create button
function setCreateButtonIsDisabled(createButtonRef: any, disabled: boolean) {
    if (createButtonRef && createButtonRef.current) {
        createButtonRef.current.disabled = disabled;
    }
}
